import { CloseOutlined } from '@mui/icons-material';
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton, styled } from '@mui/material';

import { SwapModalData } from '@/hooks/swapRoles/useDistributorSwapModal';

interface DistributorRoleSwapModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  swapModalData: SwapModalData | null;
  loading?: boolean;
}

const SwapRoleDialog = styled(Dialog)(({ theme }) => {
  const textColor = theme.hhglobal.purpose.textStrong;
  const borderColor = theme.hhglobal.purpose.strokeWeak;
  const backgroundColor = theme.hhglobal.purpose.fillWeak;

  return {
    '& .MuiDialog-paper': {
      borderRadius: 0,
    },
    '& .MuiDialogTitle-root': {
      backgroundColor,
      color: textColor,
      fontSize: theme.hhglobal.typography.headings.h2.fontSize,
      fontWeight: theme.hhglobal.typography.fontWeights.regular,
    },
    '& .MuiDialogContentText-root': {
      color: textColor,
    },
    '& .MuiDialogContent-root': {
      padding: '32px 16px',
      color: textColor,
    },
    '& .MuiDialogActions-root': {
      padding: theme.hhglobal.spacing.spacing[3].valuePx,
      borderTop: `1px solid ${borderColor}`,
      color: textColor,
    },
  };
});

/**
 * Modal dialog for confirming the reassignment of the Distributor role
 * from one group to another. When confirmed, the user's role in the current
 * group is updated to Member, and they are assigned as Distributor in the target group.
 */
export function DistributorRoleSwapModal({ open, onClose, onConfirm, swapModalData, loading = false }: DistributorRoleSwapModalProps) {
  const handleClose = (_event: React.SyntheticEvent<unknown>, reason: string) => {
    if (loading || reason === 'backdropClick') {
      return;
    }
    onClose();
  };

  return (
    <SwapRoleDialog
      open={open}
      onClose={handleClose}
      maxWidth='sm'
      fullWidth
      aria-labelledby='distributor-role-swap-modal-title'
      aria-describedby='distributor-role-swap-modal-description'
      data-testid='distributor-swap-modal'
    >
      <DialogTitle id='distributor-role-swap-modal-title'>Update user role</DialogTitle>
      <IconButton
        aria-label='close modal'
        data-testid='distributor-swap-modal-close'
        onClick={(e) => handleClose(e, 'escapeKeyDown')}
        sx={(theme) => ({
          position: 'absolute',
          right: 8,
          top: 8,
          color: theme.hhglobal.purpose.textStrong,
        })}
      >
        <CloseOutlined
          sx={(theme) => ({
            fontSize: '14px',
            color: theme.hhglobal.purpose.textStrong,
          })}
        />
      </IconButton>
      <DialogContent>
        <DialogContentText id='distributor-role-swap-modal-description'>
          Distributor is a unique role. <strong>{swapModalData?.userEmail}</strong> is currently assigned as a Distributor in{' '}
          {swapModalData?.currentGroupType} <strong>{swapModalData?.currentGroupName}</strong>. Proceeding will update their role in that
          group to Member and assign them as Distributor in this group.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onClose}
          disabled={loading}
          data-testid='distributor-swap-modal-cancel'
          sx={(theme) => ({
            color: theme.hhglobal.purpose.textStrong,
            borderRadius: theme.hhglobal.spacing.borderRadius[1],
            border: `1px solid ${theme.hhglobal.purpose.strokeStrong}`,
            boxShadow: 'none',
          })}
        >
          Cancel
        </Button>
        <Button
          onClick={onConfirm}
          variant='contained'
          disabled={loading}
          autoFocus
          data-testid='distributor-swap-modal-confirm'
          sx={(theme) => ({
            borderRadius: theme.hhglobal.spacing.borderRadius[1],
            background: theme.hhglobal.purpose.buttonStrong,
          })}
        >
          {loading ? 'Processing...' : 'Update Role'}
        </Button>
      </DialogActions>
    </SwapRoleDialog>
  );
}
